import { expertRulesPath } from "@/lib/businessRules";
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasApprovedExpertAccess } from "@/lib/expertAccess";
import ExpertProfileForm, { expertFormValues } from "@/components/profile/ExpertProfileForm";

export default function ExpertOnboarding() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const profile = user?.expert_profile;
  const [categories, setCategories] = useState([]);
  const [categoriesError, setCategoriesError] = useState("");

  const loadCategories = () => {
    setCategoriesError("");
    api.get("/categories")
      .then((response) => setCategories(response.data))
      .catch(() => setCategoriesError("Fagområderne kunne ikke indlæses."));
  };

  useEffect(() => { loadCategories(); }, []);

  const initialValues = useMemo(
    () => expertFormValues(user, profile),
    [user, profile],
  );

  const submit = async (payload) => {
    await api.post("/expert/profile/submit", payload);
    await refresh();
    navigate("/expert/afventer-godkendelse", { replace: true });
  };

  const handleLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  if (hasApprovedExpertAccess(user)) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16">
        <div className="card-outline p-8">
          <h1 className="text-3xl font-bold text-brand-ink">Din ekspertprofil er godkendt</h1>
          <p className="mt-3 text-brand-body">Profilen er synlig for kunder under Find ekspert.</p>
          <Link to="/expert?tab=profile" className="btn-primary inline-flex mt-6">Rediger min profil</Link>
        </div>
      </div>
    );
  }

  if (profile?.approval_status === "pending") {
    return <Navigate to="/expert/afventer-godkendelse" replace />;
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-10 sm:py-16">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <div className="chip mb-3">Ekspertprofil</div>
          <h1 className="text-3xl sm:text-4xl font-bold text-brand-ink">Opret din ekspertprofil</h1>
          <p className="mt-2 text-brand-body">
            Udfyld alle felter. Din profil bliver først sendt til SpørgMig, når du trykker på knappen nederst.
          </p>
        </div>
        <button
          type="button"
          onClick={handleLogout}
          className="inline-flex min-h-11 shrink-0 items-center gap-2 rounded-xl border border-brand-line bg-white px-4 text-sm font-medium text-[#991B1B] hover:bg-[#FEF2F2]"
          data-testid="expert-onboarding-logout"
        >
          <LogOut size={16}/> Log ud
        </button>
      </div>

      <p className="mb-6 text-sm text-brand-body"><Link to={expertRulesPath} target="_blank" rel="noopener noreferrer" className="text-brand-green underline underline-offset-4">Se regler for hjælpere</Link> før du sender din profil til godkendelse (åbner i en ny fane).</p>

      <ExpertProfileForm
        initialValues={initialValues}
        categories={categories}
        categoriesError={categoriesError}
        onRetryCategories={loadCategories}
        onSubmit={submit}
        submitLabel="Send profil til godkendelse"
        submittingLabel="Sender…"
        testId="expert-onboarding-form"
      />
    </div>
  );
}

